import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Alert, Box, Checkbox, DialogContentText, FormControlLabel, Link, Tooltip } from '@mui/material';
import ButtonCustom from 'components/ButtonCustom';
import GenericDialog from 'components/GenericDialog';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { logModIdfDownload } from 'services/inspections-service';
import { getRevisionByIdWithProject } from 'services/revisions';
import { USER_ROLES } from 'utils/constants';
import { baseURL } from 'utils/AxiosInstance';
import { IRevisionWithProjectResponse } from 'utils/contracts';
import { getLatestExpiryDate } from 'utils/utils';
import ProjectBanner from '../components/ProjectBanner';
import UseAuthContext from '../context/AuthContext';

const decodeRevisionIdFromCode = (code: string | null): string | null => {
  if (!code) return null;
  try {
    return atob(code);
  } catch {
    return null;
  }
};

const IframeIDFViewer: React.FC = () => {
  const { isAuthenticated, auth, organizationId, role, organizations } = useContext(UseAuthContext);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const revisionIdParam = searchParams.get('revisionId');
  const projectId = searchParams.get('projectId');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const revisionId = revisionIdParam || decodeRevisionIdFromCode(code);

  const canDownloadIdf =
    role === USER_ROLES.ADMIN ||
    role === USER_ROLES.INSPECTOR ||
    role === USER_ROLES.INSPECTOR_MANAGER;

  const { data: revisionWithProjectData, isLoading } = useQuery<IRevisionWithProjectResponse>(
    ['getRevisionByIdWithProject', revisionId, projectId],
    () => getRevisionByIdWithProject(revisionId, projectId, organizationId),
    {
      enabled:
        !!revisionId &&
        (!!projectId ||
          role === USER_ROLES.INSPECTOR ||
          role === USER_ROLES.INSPECTOR_MANAGER ||
          role === USER_ROLES.ADMIN) &&
        (!!organizationId ||
          role === USER_ROLES.INSPECTOR ||
          role === USER_ROLES.INSPECTOR_MANAGER ||
          role === USER_ROLES.ADMIN),
    }
  );

  const hasModIdf = revisionWithProjectData?.has_mod_idf === true;
  const hasIdf = revisionWithProjectData?.has_idf === true;
  const skipOverwrite = revisionWithProjectData?.skip_overwrite === true;

  const canDownloadModIdf = hasModIdf && !skipOverwrite;
  const canDownloadPlainIdf = hasIdf;
  const modIdfDownloadAllowed =
    role === USER_ROLES.ADMIN || revisionWithProjectData?.mod_idf_download_allowed === true;

  // Same unlock rules as revision list / download.js
  const latestExpiryDate = organizations ? getLatestExpiryDate(organizations) : null;
  const startedAt = revisionWithProjectData?.startedAt;
  const isRevisionStartedBeforeExpiry =
    !!latestExpiryDate && !!startedAt && new Date(startedAt) <= latestExpiryDate;
  const hasPaidAccess =
    role === USER_ROLES.ADMIN ||
    !!revisionWithProjectData?.transaction_id ||
    !!organizations?.hasUnlimited ||
    isRevisionStartedBeforeExpiry;

  // Flag and/or paid access unlock download. skip_overwrite → plain idf; else → mod_idf.
  const showDownloadButton =
    canDownloadIdf &&
    !isLoading &&
    revisionWithProjectData != null &&
    (canDownloadModIdf || canDownloadPlainIdf) &&
    (modIdfDownloadAllowed || hasPaidAccess);

  const isDownloadEnabled =
    showDownloadButton &&
    !!code &&
    !!auth?.token &&
    ((canDownloadModIdf && revisionWithProjectData?.revision_status === 'success') ||
      (canDownloadPlainIdf &&
        !canDownloadModIdf &&
        (role === USER_ROLES.ADMIN || revisionWithProjectData?.revision_status === 'success')));

  const postToIframe = (message: Record<string, unknown>) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  };

  const handleIframeLoad = () => {
    if (!iframeRef.current || !auth?.token) return;
    postToIframe({ type: 'SET_TOKEN', token: auth.token, organizationId: organizationId || null });
    setTimeout(() => postToIframe({ type: 'SET_TOKEN', token: auth.token, organizationId: organizationId || null }), 500);
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const allowedOrigins = [
        window.location.origin,
        process.env.REACT_APP_HOST || 'http://localhost:3000',
      ];
      if (!allowedOrigins.includes(event.origin)) return;
      if (event.data.type === 'TOKEN_SET') {
        console.log('Token successfully set in IDF viewer');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      const currentPath = location.pathname + location.search;
      navigate('/login', {
        state: {
          redirect: currentPath,
          message: 'LOGIN_REDIRECT_IDF_MESSAGE',
        },
      });
    }
  }, [isAuthenticated, navigate, location]);

  const buildIdfDownloadUrl = () => {
    if (!revisionWithProjectData || !auth?.token) return null;
    const { organization_id, project_id, revision_id } = revisionWithProjectData;
    return `${baseURL}/pipe/${organization_id}/${project_id}/${revision_id}/download/idf?authorization=${encodeURIComponent(auth.token)}`;
  };

  const handleDownloadClick = () => {
    if (canDownloadModIdf) {
      setDownloadError(null);
      setConsentChecked(false);
      setConsentOpen(true);
      return;
    }
    const downloadUrl = buildIdfDownloadUrl();
    if (downloadUrl) {
      window.location.href = downloadUrl;
    }
  };

  const handleConfirmDownload = async () => {
    if (!code || !consentChecked) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const { downloadUrl } = await logModIdfDownload(code, consentChecked);
      setConsentOpen(false);
      setConsentChecked(false);
      window.location.href = downloadUrl;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('IDF_VIEWER_DOWNLOAD_ERROR');
      setDownloadError(message);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!code) {
    return (
      <Box sx={{ p: 2, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">
          No revision code provided. Please add ?code=YOUR_CODE to the URL.
        </Alert>
      </Box>
    );
  }

  if (
    role !== USER_ROLES.ADMIN &&
    role !== USER_ROLES.INSPECTOR &&
    role !== USER_ROLES.INSPECTOR_MANAGER
  ) {
    return (
      <Box
        sx={{
          p: 2,
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Alert severity="error">{t('INSPECTOR_ACCESS_DENIED')}</Alert>
        <ButtonCustom variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('INSPECTOR_BUTTON_BACK_TO_PROJECTS')}
        </ButtonCustom>
      </Box>
    );
  }

  if (
    revisionWithProjectData &&
    organizationId &&
    organizationId !== revisionWithProjectData.organization_id &&
    role !== USER_ROLES.ADMIN &&
    role !== USER_ROLES.INSPECTOR &&
    role !== USER_ROLES.INSPECTOR_MANAGER
  ) {
    return (
      <Box
        sx={{
          p: 2,
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Alert severity="error">{t('3D_VIEWER_ACCESS_DENIED')}</Alert>
        <ButtonCustom variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('3D_VIEWER_BUTTON_BACK_TO_PROJECTS')}
        </ButtonCustom>
      </Box>
    );
  }

  const backendHost = process.env.REACT_APP_HOST || 'http://localhost:3000';
  const orgQuery = organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : '';
  const viewerUrl = `${backendHost}/idf/viewer?code=${code}${orgQuery}`;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ProjectBanner
        isLoading={isLoading}
        revisionWithProjectData={revisionWithProjectData}
        headerActions={
          showDownloadButton ? (
            <Tooltip title={t('IDF_VIEWER_DOWNLOAD_BUTTON')} placement="bottom">
              <span>
                <ButtonCustom
                  variant="contained"
                  disabled={!isDownloadEnabled || isDownloading}
                  onClick={handleDownloadClick}
                  sx={{
                    minWidth: '56px',
                    width: '56px',
                    height: '56px',
                    borderRadius: '8px',
                    px: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textTransform: 'none',
                  }}
                  aria-label={t('IDF_VIEWER_DOWNLOAD_BUTTON')}
                >
                  <FileDownloadOutlinedIcon />
                </ButtonCustom>
              </span>
            </Tooltip>
          ) : null
        }
      />

      <Box sx={{ flexGrow: 1, marginTop: '80px', overflow: 'hidden' }}>
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          width="100%"
          height="100%"
          title="IDF Viewer"
          style={{
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            minHeight: 'calc(100vh - 80px)',
          }}
          onLoad={handleIframeLoad}
          onError={(e) => console.error('IDF Viewer iframe error:', e)}
        />
      </Box>

      <GenericDialog
        open={consentOpen}
        onClose={() => {
          if (!isDownloading) {
            setConsentOpen(false);
            setConsentChecked(false);
          }
        }}
        title={t('IDF_VIEWER_DOWNLOAD_CONSENT_TITLE')}
        cancelButtonText={t('CANCEL_BUTTON')}
        confirmButtonText={t('IDF_VIEWER_DOWNLOAD_CONSENT_CONFIRM')}
        onConfirm={handleConfirmDownload}
        isLoading={isDownloading}
        confirmDisabled={!consentChecked}
        maxWidth={false}
        paperSx={{ maxWidth: 570, width: '100%' }}
        titleAlign="flex-start"
        contentAlign="flex-start"
        actionsAlign="center"
      >
        <DialogContentText sx={{ color: '#374151', lineHeight: 1.5, textAlign: 'start', width: '100%' }}>
          {t('IDF_VIEWER_DOWNLOAD_CONSENT_BODY_1')}
        </DialogContentText>
        <DialogContentText sx={{ color: '#374151', lineHeight: 1.5, mt: 2, textAlign: 'start', width: '100%' }}>
          {t('IDF_VIEWER_DOWNLOAD_CONSENT_BODY_2')}
        </DialogContentText>
        <FormControlLabel
          sx={{
            alignSelf: 'flex-start',
            width: '100%',
            alignItems: 'center',
            mx: 0,
            mt: 2,
            gap: 0.5,
            '& .MuiFormControlLabel-label': {
              lineHeight: 1.5,
              fontSize: '18px',
              color: '#374151',
            },
          }}
          control={
            <Checkbox
              size="small"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              color="primary"
              disabled={isDownloading}
              sx={{
                p: 0.25,
                '& .MuiSvgIcon-root': {
                  fontSize: 20,
                },
              }}
            />
          }
          label={
            <Trans
              i18nKey="IDF_VIEWER_DOWNLOAD_CONSENT_BODY_3"
              components={{
                termsLink: (
                  <Link
                    href="https://designbuilder.co.il/policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    underline="always"
                  />
                ),
              }}
            />
          }
        />
        {downloadError && (
          <DialogContentText sx={{ color: 'error.main', mt: 1, textAlign: 'start', width: '100%' }}>{downloadError}</DialogContentText>
        )}
      </GenericDialog>
    </Box>
  );
};

export default IframeIDFViewer;
